'use client';

import { APP_ASSETS } from '@/constant/assets';
import { useEffect, useState } from 'react';

const SLIDES = APP_ASSETS.LIST_BG_AUTH.map((src, i) => ({
    highSrc: src,
    lowestSrc:
        APP_ASSETS.LIST_BG_AUTH_LOWEST_RES[i] ??
        APP_ASSETS.LIST_BG_AUTH_LOW_RES[i] ??
        src,
}));

const INTERVAL_MS = 5000;

export function SsoLeftBackground() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [highResLoaded, setHighResLoaded] = useState<boolean[]>(
        SLIDES.map(() => false)
    );

    // Preload all high-res images in background; swap in when ready
    useEffect(() => {
        SLIDES.forEach((slide, i) => {
            const img = new Image();
            img.src = slide.highSrc;
            img.onload = () =>
                setHighResLoaded((prev) => {
                    const next = [...prev];
                    next[i] = true;
                    return next;
                });
        });
    }, []);

    // Auto-advance carousel
    useEffect(() => {
        if (SLIDES.length <= 1) return;
        const timer = setInterval(
            () => setCurrentIndex((i) => (i + 1) % SLIDES.length),
            INTERVAL_MS
        );
        return () => clearInterval(timer);
    }, []);

    return (
        // CSS width matches the sso-left-spacer div in SsoWrapper (same % base = 100vw) — no JS needed
        <div
            aria-hidden='true'
            className='bg-primary fixed left-0 top-0 h-screen overflow-hidden select-none max-lg:hidden lg:w-[53%] lg:min-w-[53%] xl:w-[60%]'
        >
            {SLIDES.map((slide, i) => (
                <div
                    key={i}
                    className='pointer-events-none absolute inset-0 select-none'
                    style={{
                        opacity: i === currentIndex ? 1 : 0,
                        transition: 'opacity 1.2s ease-in-out',
                    }}
                >
                    {/* Lowest-res placeholder: tiny file, loads instantly — eliminates white flash */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src={slide.lowestSrc}
                        alt=''
                        loading='eager'
                        className='bg-login absolute inset-0 h-screen w-full object-cover object-center'
                        style={{ opacity: 0.9 }}
                    />
                    {/* High-res layer: fades in on top once fully loaded */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src={slide.highSrc}
                        alt=''
                        loading={i === 0 ? 'eager' : 'lazy'}
                        className='bg-login absolute inset-0 h-screen w-full object-cover object-center'
                        style={{
                            opacity: highResLoaded[i] ? 0.9 : 0,
                            transition: 'opacity 0.8s ease-in-out',
                        }}
                    />
                </div>
            ))}

            {SLIDES.length > 1 && (
                <div className='absolute bottom-6 left-0 right-0 z-10 flex justify-center gap-2'>
                    {SLIDES.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentIndex(i)}
                            aria-label={`Slide ${i + 1}`}
                            className='h-1.5 rounded-full bg-white transition-all duration-300'
                            style={{
                                width: i === currentIndex ? '24px' : '6px',
                                opacity: i === currentIndex ? 1 : 0.45,
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
